import { ContactUs } from "@App/Components/ContactUs";
import { BrandFilter } from "@App/Components/Filters";
import { Hero } from "@App/Components/Hero";
import { Loader } from "@App/Components/Loader";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { useServices } from "@App/hooks/api/useServices";
import { useHome } from "@App/hooks/useHome";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { TireServiceStep } from "@App/Pages/Partner/Partials/TireServiceStep";
import { ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored } from "@App/utils/conditionals/combinations";
import { ServiceIsInspection } from "@App/utils/conditionals/services";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    bevSupport,
    brand,
  } = useHome();

  useEffect(() => {
    if (currentStep === 0 && service) {
      nextStep();
    }
  }, [service]);

  const locationStepProps = useLocationStepDefinition(onSubmit);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader />}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Welchen Service benötigen Sie?"}
                subtitle={null}
                completedTitle={"Reifenservice" + ": "}
                completedGoBackLinkTitle={tireStepSelection}
              >
                <TireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={false}
                />
              </Step>
            )}
          {showSecondaryTireStep &&
            ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {(availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices?.length === 0 ? "Keine Auswahl" : ""} ${premiumServices?.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices ?? []}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Waehlen Sie Ihre Marke.")}
              completedTitle={"Marke: "}
              completedGoBackLinkTitle={brand ? brand.name : ""}
            >
              <BrandFilter onSubmit={selectBrand} />
            </Step>
          )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              {organization && <Benefits organization={organization} />}
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
